import {ref} from 'vue'
import {redeemScan} from '@/services/redeemScan.js'
import {useHaptics} from '@/composables/useHaptics.js'

/**
 * The redeem flow every scan screen shares: one v3 redeem at a time, then the
 * success or error modal, then the screen's refresh. The refresh runs after
 * the outcome is shown and outside the error path: the guest is already
 * redeemed, so a failed refresh must not show an error (or invite a second
 * handover).
 *
 * @param {object} [options]
 * @param {() => Promise<unknown>} [options.refresh] reloads the screen's lists
 */
export function useScanRedeem({refresh} = {}) {
    const {successFeedback, errorFeedback} = useHaptics()
    // The screen's own busy flag (it may set it while it gathers location);
    // inFlight is the redeem's, so a double tap never posts twice.
    const processing = ref(false)
    let inFlight = false
    const error = ref(null)
    const showSuccessModal = ref(false)
    const showErrorModal = ref(false)

    /**
     * @returns {Promise<{outcome: string, duplicate: boolean, response: object|null}|null>} null when refused or busy
     */
    async function redeem(endpoint, payload) {
        if (inFlight) return null
        inFlight = true
        processing.value = true
        error.value = null
        let result
        try {
            result = await redeemScan(endpoint, payload)
        } catch (e) {
            error.value = e
            showErrorModal.value = true
            errorFeedback()
            return null
        } finally {
            inFlight = false
            processing.value = false
        }

        showSuccessModal.value = true
        successFeedback()
        try {
            await refresh?.()
        } catch (e) {
            console.warn('Refresh after a redeem failed:', e)
        }
        return result
    }

    return {processing, error, showSuccessModal, showErrorModal, redeem}
}
